import React, { useMemo } from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { ThreeCanvas } from "@remotion/three";
import * as THREE from "three";

interface SpatialBackgroundProps {
  isWordActive: boolean;
}

export const SpatialBackground: React.FC<SpatialBackgroundProps> = ({
  isWordActive,
}) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // Generate a deterministic particle field
  const particles = useMemo(() => {
    const temp = [];
    // Deterministic random seed generation to prevent visual shifting
    let seed = 42;
    const random = () => {
      const x = Math.sin(seed++) * 10000;
      return x - Math.floor(x);
    };

    for (let i = 0; i < 250; i++) {
      const x = (random() - 0.5) * 20;
      const y = (random() - 0.5) * 30;
      const z = (random() - 0.5) * 20;
      temp.push(x, y, z);
    }
    return new Float32Array(temp);
  }, []);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 1,
        background:
          "radial-gradient(circle at center, #0f0c22 0%, #030107 100%)",
      }}
    >
      <ThreeCanvas width={width} height={height}>
        {/* Lights */}
        <ambientLight intensity={0.6} />
        <pointLight position={[10, 15, 10]} intensity={2.0} color="#00f2fe" />
        <pointLight
          position={[-10, -15, -10]}
          intensity={1.5}
          color="#ff007f"
        />

        {/* Scene Container */}
        <SceneContent
          frame={frame}
          isWordActive={isWordActive}
          particles={particles}
        />
      </ThreeCanvas>
    </div>
  );
};

// Internal scene content executing inside the R3F canvas context
const SceneContent: React.FC<{
  frame: number;
  isWordActive: boolean;
  particles: Float32Array;
}> = ({ frame, isWordActive, particles }) => {
  // 1. Slow, Majestic organic 3D camera drift (No high-frequency jittery vibrations)
  const rotationY = (frame * Math.PI) / 500;
  const rotationX = Math.sin((frame * Math.PI) / 900) * 0.12;
  const rotationZ = (frame * Math.PI) / 1500;

  // 2. Smooth central wireframe sphere pulse (Low-frequency, elegant breathing pulse when speaking)
  const baseScale = 2.2;
  const pulseFactor = isWordActive ? Math.sin(frame * 0.3) * 0.06 + 0.03 : 0;
  const sphereScale = baseScale + pulseFactor;

  // 3. Smooth orbiting satellites (Constant, majestic circular orbit and subtle size swell)
  const nodeScale = isWordActive ? 0.3 : 0.22;
  const orbitAngle = (frame * Math.PI) / 180;

  // 4. Starfield particles (Subtle glow adjustment when active)
  const particleSize = isWordActive ? 0.1 : 0.07;
  const particleOpacity = isWordActive ? 0.8 : 0.6;

  return (
    <group rotation={[rotationX, rotationY, rotationZ]}>
      {/* 1. Tilted 3D Grid Floor */}
      <gridHelper
        args={[40, 40, "#ff007f", "#331040"]}
        position={[0, -8, 0]}
        rotation={[0.05, 0, 0]}
      />

      {/* 2. Holographic Dodecahedron Wireframe Orb */}
      <mesh scale={[sphereScale, sphereScale, sphereScale]}>
        <dodecahedronGeometry args={[1, 1]} />
        <meshBasicMaterial
          color="#00f2fe"
          wireframe
          transparent
          opacity={0.4}
        />
      </mesh>

      {/* 3. Outer Neon Rings */}
      <mesh
        rotation={[Math.PI / 2, 0, 0]}
        scale={[sphereScale * 1.5, sphereScale * 1.5, 1]}
      >
        <ringGeometry args={[0.96, 1, 64]} />
        <meshBasicMaterial
          color="#ff007f"
          transparent
          opacity={0.5}
          side={THREE.DoubleSide}
        />
      </mesh>

      <mesh
        rotation={[Math.PI / 4, Math.PI / 4, 0]}
        scale={[sphereScale * 1.35, sphereScale * 1.35, 1]}
      >
        <ringGeometry args={[0.98, 1, 64]} />
        <meshBasicMaterial
          color="#ffd166"
          transparent
          opacity={0.35}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* 4. Tilted Satellite Nodes (Scale and Orbit react to audio speech) */}
      <mesh
        position={[Math.sin(orbitAngle) * 4, 0, Math.cos(orbitAngle) * 4]}
        scale={[nodeScale, nodeScale, nodeScale]}
      >
        <sphereGeometry args={[1, 16, 16]} />
        <meshBasicMaterial color="#00f2fe" />
      </mesh>

      <mesh
        position={[
          Math.sin(orbitAngle + Math.PI) * 4,
          0,
          Math.cos(orbitAngle + Math.PI) * 4,
        ]}
        scale={[nodeScale, nodeScale, nodeScale]}
      >
        <sphereGeometry args={[1, 16, 16]} />
        <meshBasicMaterial color="#ff007f" />
      </mesh>

      {/* 5. 3D Floating Particle Cloud (Glows larger and brighter during active speech) */}
      <points>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[particles, 3]} />
        </bufferGeometry>
        <pointsMaterial
          color="#00f2fe"
          size={particleSize}
          sizeAttenuation
          transparent
          opacity={particleOpacity}
        />
      </points>
    </group>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
